import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import StepProgress from '../../components/StepProgress';
import FormField from '../../components/FormField';
import { apiFetch, ApiError } from '../../api/client';
import { useMyDIDs } from '../../api/hooks';

const orgTypeIds = ['Government', 'Financial', 'Healthcare', 'Education', 'Corporate', 'Non-Profit'];

/** Accepts http(s) URLs with a host; rejects bare words and other schemes. */
function isValidWebsite(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.');
  } catch {
    return false;
  }
}

export default function IssuerOnboarding() {
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState({ name: '', type: '', website: '', description: '' });
  const [touched, setTouched] = useState(false);
  const [didMethod, setDidMethod] = useState<'create' | 'import'>('create');
  const [importDid, setImportDid] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { t } = useTranslation(['issuer', 'common']);

  // The issuer DID must be one the applicant actually controls. The form
  // previously fabricated a did:adi:issuer:<slug>-<random> string that existed
  // nowhere, then never submitted anything — the whole flow was a mockup.
  const { data: myDIDs } = useMyDIDs();
  const ownDid = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did ?? '';
  const generatedDid = ownDid;
  const chosenDid = didMethod === 'create' ? ownDid : importDid.trim();

  // Step 0 gate. Website is required with a real URL — it is how a reviewer
  // checks the organisation exists — and the description doubles as the
  // application's purpose, which the API requires.
  const websiteOk = isValidWebsite(org.website);
  const step0Valid = !!org.name.trim() && !!org.type && websiteOk && !!org.description.trim();

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await apiFetch('/auth/role-applications', {
        method: 'POST',
        body: JSON.stringify({
          requestedRole: 'issuer',
          orgName: org.name.trim(),
          orgType: org.type,
          website: org.website.trim(),
          purpose: org.description.trim(),
        }),
      });
      setSubmitted(true);
    } catch (e) {
      // 409 APPLICATION_EXISTS: the server enforces one pending/approved
      // application per user and role, so resubmitting is refused rather
      // than creating a duplicate request.
      if (e instanceof ApiError && e.status === 409 && e.code === 'ORG_APPLICATION_EXISTS') {
        // Another account already has a pending/approved application for this
        // organisation.
        setSubmitError(t('onboarding.errors.orgDuplicate', {
          defaultValue: 'This organisation already has a pending or approved issuer application. Contact your organisation\'s existing issuer or a platform admin.',
        }));
      } else if (e instanceof ApiError && e.status === 409) {
        setSubmitError(t('onboarding.errors.duplicate', {
          defaultValue: 'You already have a pending or approved issuer application. You can track it under Apply for Role.',
        }));
      } else {
        setSubmitError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { label: t('onboarding.steps.org.label'), description: t('onboarding.steps.org.description') },
    { label: t('onboarding.steps.did.label'), description: t('onboarding.steps.did.description') },
    { label: t('onboarding.steps.review.label'), description: t('onboarding.steps.review.description') },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('onboarding.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('onboarding.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <StepProgress steps={steps} currentStep={submitted ? 3 : step} />
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-emerald-50">
              <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 32 }}>check_circle</span>
            </div>
            <h2 className="text-lg font-bold text-text-main">{t('onboarding.submitted.title')}</h2>
            <p className="mt-2 text-sm text-text-muted">{t('onboarding.submitted.subtitle')}</p>
            <div className="mx-auto mt-6 max-w-sm rounded-lg border border-border-subtle bg-slate-50 p-4 text-left">
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.organization')}</span>
                  <span className="font-bold text-text-main">{org.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.type')}</span>
                  <span className="font-bold text-text-main">{org.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.did')}</span>
                  <span className="font-mono font-bold text-primary">{didMethod === 'create' ? generatedDid : importDid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.status')}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-100 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                    <span className="size-1.5 rounded-full bg-amber-500" />
                    {t('common:status.pendingReview')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : step === 0 ? (
          <div className="space-y-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('onboarding.step0.heading')}</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FormField
                label={`${t('onboarding.step0.name')} *`}
                placeholder={t('onboarding.step0.namePlaceholder')}
                value={org.name}
                onChange={(e) => setOrg({ ...org, name: (e.target as HTMLInputElement).value })}
              />
              <FormField
                as="select"
                label={`${t('onboarding.step0.type')} *`}
                value={org.type}
                onChange={(e) => setOrg({ ...org, type: (e.target as HTMLSelectElement).value })}
              >
                <option value="">{t('onboarding.step0.selectType')}</option>
                {orgTypeIds.map((tt) => (
                  <option key={tt} value={tt}>{t(`onboarding.orgTypes.${tt}`)}</option>
                ))}
              </FormField>
              <FormField
                label={`${t('onboarding.step0.website')} *`}
                placeholder={t('onboarding.step0.websitePlaceholder')}
                type="url"
                value={org.website}
                onChange={(e) => setOrg({ ...org, website: (e.target as HTMLInputElement).value })}
                onBlur={() => setTouched(true)}
                error={touched && org.website && !websiteOk
                  ? t('onboarding.errors.website', { defaultValue: 'Enter a full URL, e.g. https://example.org' })
                  : undefined}
              />
            </div>
            <FormField
              as="textarea"
              label={`${t('onboarding.step0.description')} *`}
              placeholder={t('onboarding.step0.descriptionPlaceholder')}
              value={org.description}
              onChange={(e) => setOrg({ ...org, description: (e.target as HTMLTextAreaElement).value })}
              hint={t('onboarding.step0.descriptionHint', { defaultValue: 'Used as the purpose of your application — say what you intend to issue.' })}
            />
            <p className="text-xs text-text-faint">
              {t('onboarding.step0.requiredNote', { defaultValue: '* required' })}
            </p>
            <div className="flex justify-end">
              <button
                className="btn-primary"
                onClick={() => { setTouched(true); if (step0Valid) setStep(1); }}
                disabled={!step0Valid}
              >
                {t('common:actions.continue')}
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            </div>
          </div>
        ) : step === 1 ? (
          <div className="space-y-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('onboarding.step1.heading')}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <button
                onClick={() => setDidMethod('create')}
                className={`rounded-xl border-2 p-5 text-left transition-all ${
                  didMethod === 'create' ? 'border-primary bg-blue-50/50' : 'border-border-subtle hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                    <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>add_circle</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-text-main">{t('onboarding.step1.createTitle')}</p>
                    <p className="text-[10px] text-text-faint">{t('onboarding.step1.createDesc')}</p>
                  </div>
                </div>
                {didMethod === 'create' && (
                  <div className="mt-4 rounded-lg border border-border-subtle bg-white p-3">
                    {generatedDid ? (
                      <>
                        <span className="text-[10px] font-bold text-text-faint">{t('onboarding.step1.generated')}</span>
                        <p className="mt-1 break-all font-mono text-xs font-semibold text-primary">{generatedDid}</p>
                      </>
                    ) : (
                      <p className="text-xs text-amber-700">
                        {t('onboarding.step1.noDid', { defaultValue: "You don't have a DID yet." })}{' '}
                        <Link to="/my-dids" className="font-semibold underline" onClick={(e) => e.stopPropagation()}>
                          {t('onboarding.step1.createDidLink', { defaultValue: 'Create one under My DIDs' })}
                        </Link>
                      </p>
                    )}
                  </div>
                )}
              </button>
              <button
                onClick={() => setDidMethod('import')}
                className={`rounded-xl border-2 p-5 text-left transition-all ${
                  didMethod === 'import' ? 'border-primary bg-blue-50/50' : 'border-border-subtle hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-slate-100">
                    <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 20 }}>upload</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-text-main">{t('onboarding.step1.importTitle')}</p>
                    <p className="text-[10px] text-text-faint">{t('onboarding.step1.importDesc')}</p>
                  </div>
                </div>
                {didMethod === 'import' && (
                  <div className="mt-4">
                    <FormField
                      label={t('onboarding.step1.didLabel')}
                      placeholder={t('onboarding.step1.didPlaceholder')}
                      value={importDid}
                      onChange={(e) => setImportDid((e.target as HTMLInputElement).value)}
                    />
                  </div>
                )}
              </button>
            </div>
            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => setStep(0)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
                {t('common:actions.back')}
              </button>
              <button className="btn-primary" onClick={() => setStep(2)} disabled={!chosenDid}>
                {t('common:actions.continue')}
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('onboarding.step2.heading')}</h2>
            <div className="rounded-lg border border-border-subtle bg-slate-50 p-5">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.step2.organization')}</span>
                  <span className="font-bold text-text-main">{org.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.step2.type')}</span>
                  <span className="font-bold text-text-main">{org.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.step2.website')}</span>
                  <span className="font-bold text-primary">{org.website || t('onboarding.step2.notProvided')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.step2.did')}</span>
                  <span className="font-mono text-xs font-bold text-primary">
                    {didMethod === 'create' ? generatedDid : importDid}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.step2.method')}</span>
                  <span className="font-bold text-text-main">{didMethod === 'create' ? t('onboarding.step2.newDid') : t('onboarding.step2.imported')}</span>
                </div>
              </div>
              {org.description && (
                <div className="mt-4 border-t border-border-subtle pt-3">
                  <span className="text-xs text-text-faint">{t('onboarding.step2.description')}</span>
                  <p className="mt-1 text-sm text-text-main">{org.description}</p>
                </div>
              )}
            </div>
            {submitError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <span className="material-symbols-outlined shrink-0" style={{ fontSize: 18 }}>error</span>
                <span>{submitError}</span>
              </div>
            )}
            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => setStep(1)} disabled={submitting}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
                {t('common:actions.back')}
              </button>
              <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
                {submitting ? t('common:actions.submitting', { defaultValue: 'Submitting…' }) : t('onboarding.step2.submit')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
